import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from '@emotion/styled';
import { useRSocket } from '../../hooks/useRSocket';
import { useChatMessageStore } from '../../stores/useChatMessageStore';
import { useOptimizedInfiniteScroll } from '../../hooks/useOptimizedInfiniteScroll';
import MessageList from '../ui/MessageList';
import UserChatInput from './UserChatInput';
import { fetchMessagesForRoom } from '@edusense/people-chat/handlers/DM';
import { Avatar } from '@edusense/shared-ui-kit';
const ContainerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: #ffffff;

  .dark & {
    background-color: #1f2937;
  }
`;
const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  background-color: #ffffff;

  .dark & {
    border-color: #374151;
    background-color: #1f2937;
  }
`;
const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;
const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
`;
const UserName = styled.div`
  font-weight: 600;
  color: #1f2937;

  .dark & {
    color: #f3f4f6;
  }
`;
const OnlineStatus = styled.div`
  font-size: 0.75rem;
  color: ${(props)=>props.isOnline ? '#10b981' : '#6b7280'};
`;
const CloseButton = styled.button`
  padding: 0.5rem;
  border: none;
  background: none;
  color: #6b7280;
  cursor: pointer;
  border-radius: 0.375rem;
  transition: all 0.2s;

  &:hover {
    background-color: #f3f4f6;
    color: #374151;
  }

  .dark & {
    color: #9ca3af;

    &:hover {
      background-color: #374151;
      color: #d1d5db;
    }
  }
`;
const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;
const TypingIndicator = styled.div`
  padding: 0 1rem;
  color: #6b7280;
  font-size: 0.875rem;
  font-style: italic;
  height: 1.5rem;
  display: flex;
  align-items: center;

  .dark & {
    color: #9ca3af;
  }
`;
const EmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: #6b7280;
  text-align: center;

  .dark & {
    color: #9ca3af;
  }
`;
const UserChatContainer = ({ roomId, currentUserId, otherUserId, otherUserName, onClose })=>{
    const [replyTo, setReplyTo] = useState(null);
    const [isTyping, setIsTyping] = useState(false);
    const [otherUserTyping, setOtherUserTyping] = useState(false);
    const [isOnline, setIsOnline] = useState(false);
    const [containerHeight, setContainerHeight] = useState(400);
    const messagesEndRef = useRef(null);
    const containerRef = useRef(null);
    // 최적화된 무한 스크롤 메시지 로딩
    const { data: optimizedMessages, fetchPreviousPage, hasPreviousPage, isFetchingPreviousPage, isLoading, refetch } = useOptimizedInfiniteScroll({
        queryKey: [
            'roomMessages',
            roomId
        ],
        queryFn: ({ pageParam })=>fetchMessagesForRoom(roomId, pageParam, 20),
        enabled: !!roomId,
        staleTime: 2 * 60 * 1000,
        pageSize: 20
    });
    const { messages, reactions, typingUsers, addMessage, updateMessageStatus, addReaction, removeReaction, retryFailedMessage } = useChatMessageStore();
    const roomMessages = messages[roomId] || [];
    const roomReactions = reactions[roomId] || {};
    const roomTypingUsers = typingUsers[roomId] || [];
    // 최적화된 메시지 목록 (API + 실시간)
    const allMessages = useCallback(()=>{
        const apiMessages = optimizedMessages || [];
        const realtimeMessages = roomMessages;
        // duplicate removed하고 시간순 정렬
        const combinedMap = new Map();
        apiMessages.forEach((msg)=>combinedMap.set(msg.id, msg));
        realtimeMessages.forEach((msg)=>combinedMap.set(msg.id, msg));
        return Array.from(combinedMap.values()).sort((a, b)=>new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    }, [
        optimizedMessages,
        roomMessages
    ]);
    const finalMessages = allMessages();
    // 컨테이너 크기 측정
    useEffect(()=>{
        const updateHeight = ()=>{
            if (containerRef.current) {
                const rect = containerRef.current.getBoundingClientRect();
                setContainerHeight(rect.height - 120); // 헤더와 입력창 공간 제외
            }
        };
        updateHeight();
        window.addEventListener('resize', updateHeight);
        return ()=>window.removeEventListener('resize', updateHeight);
    }, []);
    // RSocket 연결 설정
    const { sendMessage: sendRSocketMessage, sendReaction, sendTyping, isConnected } = useRSocket({
        roomId,
        currentUserId,
        onMessageReceived: (message)=>{
            addMessage(roomId, message);
        },
        onReactionReceived: (reaction)=>{
            addReaction(roomId, reaction);
        },
        onTypingReceived: (data)=>{
            if (data.userId !== currentUserId) {
                setOtherUserTyping(data.isTyping);
            }
        },
        onPresenceReceived: (data)=>{
            if (data.userId === otherUserId) {
                setIsOnline(data.isOnline);
            }
        }
    });
    // 메시지 전송
    const handleSendMessage = useCallback(async (text, files, replyToMessage)=>{
        if (!text.trim() && (!files || files.length === 0)) return;
        const messageId = Date.now().toString();
        const timestamp = new Date();
        const newMessage = {
            id: messageId,
            text: text.trim(),
            timestamp: timestamp.toISOString(),
            sender: {
                userId: currentUserId,
                userName: currentUserId,
                displayName: '나',
                avatarUrl: undefined
            },
            roomId,
            isRead: false,
            status: 'sending',
            replyToMessageId: replyToMessage?.id,
            replyContent: replyToMessage ? `${replyToMessage.sender.displayName}: ${replyToMessage.text}` : undefined,
            mediaUrl: files?.[0] ? URL.createObjectURL(files[0]) : undefined,
            contentType: files?.[0]?.type
        };
        // 로컬 상태 업데이트
        addMessage(roomId, newMessage);
        try {
            // RSocket으로 전송
            if (isConnected) {
                await sendRSocketMessage({
                    content: text.trim(),
                    recipientId: otherUserId,
                    replyToMessageId: replyToMessage?.id,
                    files: files
                });
                // 전송 성공 시 상태 업데이트
                updateMessageStatus(roomId, messageId, 'sent');
            } else {
                throw new Error('연결이 끊어졌습니다');
            }
        } catch (error) {
            // 전송 실패 시 상태 업데이트
            updateMessageStatus(roomId, messageId, 'failed', error instanceof Error ? error.message : '알 수 없는 오류');
        }
        // 답장 상태 클리어
        setReplyTo(null);
    }, [
        roomId,
        currentUserId,
        otherUserId,
        isConnected,
        sendRSocketMessage,
        addMessage,
        updateMessageStatus
    ]);
    // 반응 추가
    const handleAddReaction = useCallback(async (messageId, reactionType)=>{
        const reaction = {
            id: Date.now(),
            messageId: Number(messageId),
            userId: currentUserId,
            displayName: '나',
            reactionType,
            timestamp: new Date()
        };
        addReaction(roomId, reaction);
        if (isConnected) {
            await sendReaction({
                messageId,
                reactionType,
                action: 'add'
            });
        }
    }, [
        roomId,
        currentUserId,
        isConnected,
        sendReaction,
        addReaction
    ]);
    // 반응 제거
    const handleRemoveReaction = useCallback(async (messageId, reactionType)=>{
        removeReaction(roomId, Number(messageId), currentUserId, reactionType);
        if (isConnected) {
            await sendReaction({
                messageId,
                reactionType,
                action: 'remove'
            });
        }
    }, [
        roomId,
        currentUserId,
        isConnected,
        sendReaction,
        removeReaction
    ]);
    // 타이핑 상태 전송
    const handleTyping = useCallback(async (typing)=>{
        setIsTyping(typing);
        if (isConnected) {
            await sendTyping({
                isTyping: typing,
                recipientId: otherUserId
            });
        }
    }, [
        isConnected,
        sendTyping,
        otherUserId
    ]);
    // 답장 핸들러
    const handleReply = useCallback((message)=>{
        setReplyTo(message);
    }, []);
    const handleCancelReply = useCallback(()=>{
        setReplyTo(null);
    }, []);
    // 메시지 재전송
    const handleRetryMessage = useCallback(async (messageId)=>{
        const message = roomMessages.find((msg)=>msg.id === messageId);
        if (!message || message.status !== 'failed') return;
        // 재전송 상태로 변경
        retryFailedMessage(roomId, messageId);
        try {
            if (isConnected) {
                await sendRSocketMessage({
                    content: message.text,
                    recipientId: otherUserId,
                    replyToMessageId: message.replyToMessageId
                });
                updateMessageStatus(roomId, messageId, 'sent');
            } else {
                throw new Error('연결이 끊어졌습니다');
            }
        } catch (error) {
            updateMessageStatus(roomId, messageId, 'failed', error instanceof Error ? error.message : '알 수 없는 오류');
        }
    }, [
        roomId,
        roomMessages,
        isConnected,
        sendRSocketMessage,
        otherUserId,
        retryFailedMessage,
        updateMessageStatus
    ]);
    // 자동 스크롤
    useEffect(()=>{
        messagesEndRef.current?.scrollIntoView({
            behavior: 'smooth'
        });
    }, [
        roomMessages
    ]);
    // 타이핑 표시 자동 해제
    useEffect(()=>{
        if (otherUserTyping) {
            const timeout = setTimeout(()=>{
                setOtherUserTyping(false);
            }, 3000);
            return ()=>clearTimeout(timeout);
        }
    }, [
        otherUserTyping
    ]);
    return /*#__PURE__*/ _jsxs(ContainerWrapper, {
        "data-testid": "people-chat-container",
        children: [
            /*#__PURE__*/ _jsxs(Header, {
                "data-testid": "people-chat-header",
                children: [
                    /*#__PURE__*/ _jsxs(UserInfo, {
                        children: [
                            /*#__PURE__*/ _jsx(Avatar, {
                                username: otherUserName,
                                name: otherUserName,
                                size: "md"
                            }),
                            /*#__PURE__*/ _jsxs(UserDetails, {
                                children: [
                                    /*#__PURE__*/ _jsx(UserName, {
                                        children: otherUserName
                                    }),
                                    /*#__PURE__*/ _jsx(OnlineStatus, {
                                        isOnline: isOnline,
                                        children: isOnline ? '온라인' : '오프라인'
                                    })
                                ]
                            })
                        ]
                    }),
                    onClose && /*#__PURE__*/ _jsx(CloseButton, {
                        onClick: onClose,
                        children: /*#__PURE__*/ _jsx("svg", {
                            className: "w-5 h-5",
                            fill: "none",
                            stroke: "currentColor",
                            viewBox: "0 0 24 24",
                            children: /*#__PURE__*/ _jsx("path", {
                                strokeLinecap: "round",
                                strokeLinejoin: "round",
                                strokeWidth: 2,
                                d: "M6 18L18 6M6 6l12 12"
                            })
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(MessagesContainer, {
                ref: containerRef,
                "data-testid": "people-chat-messages",
                children: [
                    isLoading ? /*#__PURE__*/ _jsx(EmptyState, {
                        children: /*#__PURE__*/ _jsx("div", {
                            children: "메시지를 불러오고 있습니다..."
                        })
                    }) : /*#__PURE__*/ _jsx(MessageList, {
                        messages: finalMessages,
                        currentUserId: currentUserId,
                        height: containerHeight,
                        itemSize: 80,
                        onLoadMore: fetchPreviousPage,
                        hasMore: hasPreviousPage,
                        isLoading: isFetchingPreviousPage,
                        onMessageClick: handleReply,
                        autoScrollToBottom: true
                    }),
                    otherUserTyping && /*#__PURE__*/ _jsxs(TypingIndicator, {
                        "data-testid": "people-chat-typing",
                        children: [
                            otherUserName,
                            "님이 입력 중입니다..."
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(UserChatInput, {
                onSendMessage: handleSendMessage,
                isLoading: false,
                placeholder: `${otherUserName}님에게 메시지 보내기...`,
                replyTo: replyTo,
                onCancelReply: handleCancelReply,
                onTyping: handleTyping
            })
        ]
    });
};
export default UserChatContainer;
